import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { IPlaylistActivity } from '../models/playlist-activity.model';
import { IPlaylist } from '../models/playlist.model';
import { ISharedPlaylist } from '../models/shared-playlist.model';
import { AuthService } from './auth.service'; // Added

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class PlaylistService {
  private controller = '/api/Playlist/';

  constructor(private http: HttpClient, private authService: AuthService) { } // Modified

  createPlaylist(playlist: IPlaylist, activityCsvFile: File): Observable<IPlaylist> {
    if (!playlist) {
      throw new Error('Playlist cannot be undefined at this point.');
    }
    if (!activityCsvFile) {
      throw new Error('Activity CSV cannot be undefined at this point.');
    }

    const formData: FormData = new FormData();
    const blob = new Blob([JSON.stringify(playlist)], { type: 'application/json' });
    formData.append('playlist', blob, 'playlistJson');
    formData.append('fileKey', activityCsvFile, 'playlistActivities');

    const endpoint = this.controller + `CreatePlaylistWithCsv/`;
    return this.http.post<IPlaylist>(endpoint, formData).pipe(
      tap(() => this.getPlaylistsCreated(true).subscribe()) // Force refresh cache after creation
    );
  }

  updatePlaylistMetaData(playlist: IPlaylist): Observable<IPlaylist> {
    if (!playlist) {
      throw new Error('Playlist cannot be undefined at this point.');
    }

    const endpoint = this.controller + `UpdatePlaylistMetaData/`;
    return this.http.put<IPlaylist>(endpoint, playlist, httpOptions).pipe(
      tap(() => this.getPlaylistsCreated(true).subscribe()) // Force refresh cache after updating playlist metadata
    );
  }

  updatePlaylistActivities(playlist: IPlaylist, activityCsvFile: File): Observable<IPlaylist> {
    if (!playlist) {
      throw new Error('Playlist cannot be undefined at this point.');
    }

    const formData: FormData = new FormData();
    formData.append('fileKey', activityCsvFile, 'playlistActivities');

    const endpoint = this.controller + `UpdatePlaylistActivities/${playlist.id}`;
    return this.http.put<IPlaylist>(endpoint, formData).pipe(
      tap(() => this.getPlaylistsCreated(true).subscribe()) // Force refresh cache after updating playlist activities
    );
  }

  getVersionSpecificPlaylist(playlistId: number, playlistVersion: number): Observable<IPlaylist> {
    if (!playlistId) {
      throw new Error('Playlist id cannot be undefined at the point');
    }

    const endpoint = this.controller + `GetVersionSpecificPlaylist/`;
    return this.http.get<IPlaylist>(endpoint, { params: { playlistId: playlistId.toString(), playlistVersion: playlistVersion.toString() } });
  }

  getPlaylist(playlistId: number): Observable<IPlaylist> {
    if (!playlistId) {
      throw new Error('Playlist ID cannot be undefined at this point.');
    }

    const endpoint = this.controller + 'GetPlaylist/';
    return this.http.get<IPlaylist>(endpoint, { params: { playlistId: playlistId.toString() } });
  }

  getPlaylistsAll(): Observable<IPlaylist[]> {
    const endpoint = this.controller + 'GetPlaylists/';
    return this.http.get<IPlaylist[]>(endpoint);
  }

  private _playlistsCreated: IPlaylist[] | null = null;
  
  public get STORAGE_KEY_PLAYLISTS_CREATED(): string {
    return `user_created_playlists_${this.authService.userId}`;
  }

  getPlaylistsCreated(forceRefresh: boolean = false): Observable<IPlaylist[]> {
    const endpoint = this.controller + 'GetPlaylistsCreated/';

    return new Observable<IPlaylist[]>(observer => {
      // 1. Emit cached data immediately if available and not forcing refresh
      if (!forceRefresh) {
        if (!this._playlistsCreated) {
          this._loadFromLocalStorage();
        }

        if (this._playlistsCreated) {
          observer.next(this._playlistsCreated);
        }
      }

      // 2. Fetch from network
      this.http.get<IPlaylist[]>(endpoint).subscribe({
        next: (playlists) => {
          // 3. Update cache and emit new data
          this._playlistsCreated = playlists;
          this._updateLocalStorage(playlists);
          observer.next(playlists);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        }
      });
    });
  }

  private _updateLocalStorage(playlists: IPlaylist[]): void {
    localStorage.setItem(this.STORAGE_KEY_PLAYLISTS_CREATED, JSON.stringify(playlists));
  }

  private _loadFromLocalStorage(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY_PLAYLISTS_CREATED);
    if (stored) {
      try {
        this._playlistsCreated = JSON.parse(stored);
      } catch (e) {
        console.warn('Failed to parse playlists from local storage', e);
        localStorage.removeItem(this.STORAGE_KEY_PLAYLISTS_CREATED);
      }
    }
  }

  getAvailablePlaylistVersions(playlistId: number): Observable<number[]> {
    const endpoint = this.controller + `GetAvailablePlaylistVersions/${playlistId}`;
    return this.http.get<number[]>(endpoint);
  }

  getPlaylistActivities(playlistId: number): Observable<IPlaylistActivity[]> {
    const endpoint = this.controller + 'GetPlaylistActivities/';
    return this.http.get<IPlaylistActivity[]>(endpoint, { params: { playlistId: playlistId.toString() } });
  }

  getPlaylistForUserPlaylist(userPlaylistId: number): Observable<IPlaylist> {
    const url = this.controller + 'GetPlaylistFromUserPlaylist/';
    return this.http.get<IPlaylist>(url, { params: { userPlaylistId: userPlaylistId.toString() } });
  }

  exportPlaylist(playlistId: number): Observable<Blob> {
    if (!playlistId) {
      throw new Error('Playlist ID cannot be undefined when trying to export a playlist.');
    }

    const headersText = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    const endpoint = this.controller + 'ExportPlaylist';
    const params = new HttpParams().set('playlistId', playlistId.toString());

    return this.http.get(endpoint, {
      headers: headersText,
      responseType: 'blob',
      params,
    }) as Observable<Blob>;
  }

  exportPlaylistTemplate(): Observable<Blob> {
    const endpoint = this.controller + 'ExportPlaylistTemplate/';
    const headersText = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');

    return this.http.get(endpoint, {
      headers: headersText,
      responseType: 'blob',
    }) as Observable<Blob>;
  }

  retirePlaylist(playlistId: number): Observable<number> {
    if (!playlistId) {
      throw new Error('Playlist ID cannot be undefined when trying to retire a playlist.');
    }

    const params = new HttpParams().set('id', String(playlistId));
    const options = { headers: httpOptions.headers, params };
    const endpoint = this.controller + `RetirePlaylist`;

    return this.http.delete<number>(endpoint, options).pipe(
      tap(() => this.getPlaylistsCreated(true).subscribe()) // Force refresh cache after retiring playlist
    );
  }

  unRetirePlaylist(playlistId: number): Observable<number> {
    if (!playlistId) {
      throw new Error('Playlist ID cannot be undefined when trying to unretire a playlist.');
    }

    const params = new HttpParams().set('id', String(playlistId));
    const options = { headers: httpOptions.headers, params };
    const endpoint = this.controller + `UnRetirePlaylist`;

    return this.http.delete<number>(endpoint, options).pipe(
      tap(() => this.getPlaylistsCreated(true).subscribe()) // Force refresh cache after unretiring playlist
    );
  }

  deletePlaylistAdmin(playlistId: number): Observable<number> {
    if (!playlistId) {
      throw new Error('Playlist ID cannot be undefined when trying to delete a playlist.');
    }

    const params = new HttpParams().set('id', String(playlistId));
    const options = { headers: httpOptions.headers, params };
    const endpoint = this.controller + `DeletePlaylist`;

    return this.http.delete<number>(endpoint, options);
  }

  getSharedPlaylistsForGuild(guildId: number): Observable<ISharedPlaylist[]> {
    if (!guildId) {
      throw new Error(`Guild Id can't be underfined when trying to get the shared playlists`);
    }

    const endpoint = this.controller + `GetSharedPlaylistsForGuild/${guildId}`;

    return this.http.get<ISharedPlaylist[]>(endpoint);
  }

  getUserSuggestedPlaylists(): Observable<IPlaylist[]> {
    const endpoint = this.controller + 'GetUserSuggestedPlaylists/';
    return this.http.get<IPlaylist[]>(endpoint);
  }

  sharePlaylistWithGuild(guildId: number, playlistId: number): Observable<ISharedPlaylist> {
    const endpoint = this.controller + `SharePlaylistWithGuild/${guildId}/${playlistId}`;
    return this.http.post<ISharedPlaylist>(endpoint, {});
  }

  unsharePlaylistFromGuild(sharedPlaylistId: number, guildId: number): Observable<ISharedPlaylist> {
    if (!sharedPlaylistId) {
      throw new Error(' SharedPlaylist ID cannot be undefined at this point.');
    }

    if (!guildId) {
      throw new Error('Guild ID cannot be undefined at this point.');
    }

    const params = new HttpParams().set('sharedPlaylistId', String(sharedPlaylistId)).set('guildId', String(guildId));
    const options = { headers: httpOptions.headers, params };
    const url = this.controller + `UnsharePlaylistFromGuild/`;
    return this.http.delete<ISharedPlaylist>(url, options);
  }

  updateSharedPlaylistVersion(sharedPlaylistId: number, guildId: number, updatedPlaylistVersionId: number): Observable<ISharedPlaylist> {
    if (!sharedPlaylistId) {
      throw new Error('SharedPlaylist ID cannot be undefined at this point.');
    }

    if (!guildId) {
      throw new Error('Guild ID cannot be undefined at this point.');
    }

    if (!updatedPlaylistVersionId) {
      throw new Error('We need to know the ID of the updated playlist at this point.');
    }

    const endpoint = this.controller + 'UpdateSharedPlaylistVersion/';

    return this.http.post<ISharedPlaylist>(endpoint, { options: { observe: 'response', responseType: 'json' } }, { params: { guildId: guildId.toString(), sharedPlaylistId: sharedPlaylistId.toString(), updatedPlaylistVersionId: updatedPlaylistVersionId.toString() } });
  }

  cleanEmptyPlaylistActivities(): Observable<IPlaylistActivity[]> {
    const url = this.controller + `CleanEmptyPlaylistActivities`;
    return this.http.post<IPlaylistActivity[]>(url, '', httpOptions);

  }
}
